import React, { useEffect, useMemo, useRef, useState } from "react";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import { onValue, ref } from "firebase/database";
import { useParams } from "react-router-dom";

import { database } from "../../firebase";
import RealTimeCard from "./RealTimeCard";
import { MAX_POINTS } from "./useRealtimeSeries";
import { baseChartOption, lineSeries, metricColor } from "../charts/chartTheme";
import { EM_DASH, describe, formatNumber } from "../../utils/format";

/* ECG is the one stream that cannot use the shared tail parser: the node grows
   continuously and the trace must advance at a fixed rate, so incoming samples
   are buffered and flushed at the display rate rather than re-read wholesale. */
const SAMPLES_PER_SECOND = 49;
const FLUSH_INTERVAL_MS = 1000;

/** -900..-800 and -99999 are lead-off artefacts, not signal. */
const isSignal = (value) =>
  Number.isFinite(value) && value !== -99999 && !(value >= -900 && value <= -800);

const ECGRealTime = () => {
  const { watchId } = useParams();

  const [trace, setTrace] = useState({ samples: [], values: [] });
  const [status, setStatus] = useState("");
  const [progress, setProgress] = useState(0);

  // Buffers and cursors that must not trigger re-renders.
  const buffer = useRef([]);
  const sampleIndex = useRef(0);
  const flushTimer = useRef(null);
  const charCursor = useRef(0); // characters of the node already consumed
  const carry = useRef(""); // partial token spanning two updates
  const firstAttach = useRef(true); // ignore the historical backlog once
  const skipToEnd = useRef(false); // after a session reset, jump to the tail

  useEffect(() => {
    if (!watchId) return undefined;

    const reset = () => {
      buffer.current = [];
      sampleIndex.current = 0;
      charCursor.current = 0;
      carry.current = "";
      setTrace({ samples: [], values: [] });
    };

    const unsubscribeData = onValue(
      ref(database, `Health/${watchId}/realTime/ecg`),
      (snapshot) => {
        const raw = snapshot.val();
        if (typeof raw !== "string") return;

        // Skip whatever was already in the node when we attached, otherwise the
        // whole previous capture replays before the live trace begins.
        if (firstAttach.current || skipToEnd.current) {
          charCursor.current = raw.length;
          carry.current = "";
          buffer.current = [];
          firstAttach.current = false;
          skipToEnd.current = false;
          return;
        }

        // Producer restarted and the node shrank — resync rather than reparse.
        if (raw.length < charCursor.current) {
          charCursor.current = raw.length;
          carry.current = "";
          return;
        }

        let tail = raw.slice(charCursor.current);
        if (!tail) return;
        charCursor.current = raw.length;

        if (carry.current) {
          tail = carry.current + tail;
          carry.current = "";
        }

        const parts = tail.split(",");
        // A tail not ending in a comma has a half-written number at the end.
        if (!tail.endsWith(",")) carry.current = parts.pop() || "";

        for (const part of parts) {
          if (part === "") continue;
          const value = Number(part);
          if (isSignal(value)) buffer.current.push(value);
        }
      }
    );

    const unsubscribeStatus = onValue(
      ref(database, `Health/${watchId}/realTime/ecgStatusDescription`),
      (snapshot) => setStatus(snapshot.val() || "")
    );

    const unsubscribeProgress = onValue(
      ref(database, `Health/${watchId}/realTime/ecgProgress`),
      (snapshot) => {
        const next = snapshot.val() || 0;
        setProgress(next);
        if (next === 0) {
          reset();
          skipToEnd.current = true;
        }
      }
    );

    // Flush one second of samples at a time so the trace sweeps at a steady
    // rate instead of jumping whenever a database update lands.
    flushTimer.current = setInterval(() => {
      if (!buffer.current.length) return;

      const chunk = buffer.current.splice(
        0,
        Math.min(SAMPLES_PER_SECOND, buffer.current.length)
      );

      const nextSamples = chunk.map(() => {
        sampleIndex.current += 1;
        return sampleIndex.current;
      });

      setTrace((prev) => {
        let values = prev.values.concat(chunk);
        let samples = prev.samples.concat(nextSamples);

        if (values.length > MAX_POINTS) {
          const overflow = values.length - MAX_POINTS;
          values = values.slice(overflow);
          samples = samples.slice(overflow);
        }

        return { samples, values };
      });
    }, FLUSH_INTERVAL_MS);

    return () => {
      unsubscribeData();
      unsubscribeStatus();
      unsubscribeProgress();
      if (flushTimer.current) {
        clearInterval(flushTimer.current);
        flushTimer.current = null;
      }
    };
  }, [watchId]);

  const stats = useMemo(() => describe(trace.values, 2), [trace.values]);

  const bounds = useMemo(() => {
    const numbers = trace.values.filter(Number.isFinite);
    if (!numbers.length) return { min: undefined, max: undefined };
    return {
      min: Math.floor(Math.min(...numbers)) - 1,
      max: Math.ceil(Math.max(...numbers)) + 1,
    };
  }, [trace.values]);

  const buildOption = () =>
    baseChartOption({
      categories: trace.samples,
      // No smoothing and no markers: a waveform is read by its shape.
      series: [
        lineSeries({
          name: "ECG",
          data: trace.values,
          color: metricColor("ecg"),
          area: false,
          smooth: 0,
          width: 1.4,
          showSymbol: false,
        }),
      ],
      xName: "Sample",
      yMin: bounds.min,
      yMax: bounds.max,
      tooltipFormatter: (params) => {
        const point = params?.[0];
        if (!point) return "";
        return `Sample ${point.axisValue}<br/><strong>${point.value}</strong>`;
      },
    });

  return (
    <RealTimeCard
      title="Live electrocardiogram"
      icon={<MonitorHeartOutlinedIcon />}
      accent={metricColor("ecg")}
      exportName="ecg"
      status={status}
      progress={progress}
      hasData={trace.values.length > 0}
      waitingCopy="Keep a finger on the sensor. The waveform sweeps in at 49 samples per second once the capture starts."
      deps={[trace]}
      buildOption={buildOption}
      stats={[
        {
          label: "Latest",
          value: trace.values.length
            ? formatNumber(trace.values[trace.values.length - 1], 2)
            : EM_DASH,
          meta: "Most recent sample",
        },
        { label: "Average", value: stats.avg, meta: "This capture" },
        { label: "Amplitude", value: stats.range, meta: "Min to max" },
        {
          label: "Samples",
          value: trace.values.length || EM_DASH,
          meta: "In the rolling window",
        },
      ]}
    />
  );
};

export default ECGRealTime;
